import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { useAccountClosure } from '../lib/account-closure'
import { ApiError } from '../lib/errors'
import { CancelDeletionButton, formatClosureDate } from '../components/account-closure-banner'
import { SupportEmail } from '../components/legal/legal-page'

const normalize = (s: string): string => s.trim().toLowerCase()

export default function AccountPage(): JSX.Element {
  const { user } = useAuth()
  const { closure, loading, request } = useAccountClosure()
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<'mismatch' | 'mail' | 'unknown' | 'other' | null>(null)

  const email = user?.email ?? ''
  const matches = email !== '' && normalize(typed) === normalize(email)

  async function onDelete(): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      await request(typed.trim())
    } catch (e) {
      const code = e instanceof ApiError ? e.code : ''
      setError(
        code === 'EMAIL_MISMATCH'
          ? 'mismatch'
          : code === 'CLOSURE_MAIL_FAILED'
            ? 'mail'
            : code === 'CLOSURE_STATE_UNKNOWN'
              ? 'unknown'
              : 'other',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="account-page">
      <h1 className="account-page__title">Account</h1>
      <p className="account-page__email">
        Signed in as <strong>{email}</strong>
      </p>

      <h2 className="account-page__heading">Delete my account</h2>
      {/* Above both branches: the store re-reads the server after the failure,
          so the page below may now show a deletion that does stand. */}
      {error === 'unknown' && (
        <p className="account-closure__error" role="status">
          We could not confirm whether your account deletion was scheduled. What this page shows now is what we
          know; if in doubt, write to <SupportEmail />.
        </p>
      )}
      {loading ? (
        <p>Loading…</p>
      ) : closure ? (
        <div className="account-page__section">
          <p>
            Your account will be deleted on {formatClosureDate(closure.dueAt)}. Until then you can cancel the deletion.
          </p>
          <CancelDeletionButton />
        </div>
      ) : (
        <div className="account-page__section">
          <p>
            Deleting your account deletes all your Memoirs and every photograph in them. Albums you shared stop
            working for the people you shared them with.
          </p>
          <p>
            Your account is deleted within 30 days of your request, and you can cancel any time before then. The date
            is shown as soon as you ask.
          </p>
          <p>
            Deleting your account is not a refund. Refunds are separate: write to <SupportEmail /> or refund a Memoir
            within 30 days of buying it.
          </p>
          <p>
            Download what you want to keep first: open a Memoir and use the download button on its albums. Back on{' '}
            <Link to="/">your Memoirs</Link>, you can do it for each one.
          </p>
          <label className="account-page__label" htmlFor="account-confirm-email">
            Type your email to confirm
          </label>
          <input
            id="account-confirm-email"
            className="account-page__input"
            type="email"
            autoComplete="off"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
          />
          <button
            className="account-page__delete"
            disabled={!matches || busy}
            onClick={() => void onDelete()}
          >
            {busy ? 'Deleting…' : 'Delete my account'}
          </button>
          {error && error !== 'unknown' && (
            <p className="account-closure__error" role="status">
              {error === 'mismatch' && 'That is not the email you signed in with.'}
              {error === 'mail' && (
                <>
                  We could not send you the confirmation email, so nothing was scheduled. Please try again or write
                  to <SupportEmail />.
                </>
              )}
              {error === 'other' && (
                <>
                  Something went wrong, and nothing was scheduled. Please try again or write to <SupportEmail />.
                </>
              )}
            </p>
          )}
        </div>
      )}
    </section>
  )
}
